import { Box, Button, CircularProgress, Typography } from "@mui/material";
import MeetingsTable from "./MeetingsTable";
import MeetingFormDilogContainer from "./MeetingFormDilogContainer";
import { useDispatch, useSelector } from "react-redux";
import { actionTypes } from "../../store/actionTypes/actionTypes";
import { useEffect, useState } from "react";
import { IApiResponse } from "../../interfaces/common/IApiResponse";
import { postDataFromApi } from "../../services/api/apiService";
import { RestfulUrls } from "../../constants/restfulUrls";
import { useNavigate } from "react-router-dom";
import { IMeetingReducerState } from "../../interfaces/store/IMeetingReducerState";
import LoaderWithText from "../common/ui/LoaderWithText";
import { COMMON } from "../../constants/common";
import { showSnackbar } from "../../store/actions/snackbarActions";
import { useAuth } from "../../context/AuthContext";
import { USER } from "../../constants/userCred";
//import { RouterPathEnum } from "../../constants/router";


const MeetingsContainer = () => {
  const { userRole } = useAuth();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [isImprovingPerformance, setIsImprovingPerformance] = useState(false);
  const [isDownloadingTool, setIsDownloadingTool] = useState(false);
  const meetingTableLoading = useSelector(
    (state: { meeting: IMeetingReducerState }) =>
      state.meeting.meetingTableLoading
  );

  const handleNewMeetingButtonClick = () => {
    dispatch({
      type: actionTypes.UPDATE_MEETING_OPEN,
      payload: true,
    });
  };

  const handleDownloadDesktopTool = () => {
    setIsDownloadingTool(true);
    try {
      // Get the access token for authentication
      const token = localStorage.getItem(COMMON.ACCESS_TOKEN);
      if (!token) {
        dispatch(showSnackbar("Authentication required. Please login again.", "error"));
        navigate("/login");
        setIsDownloadingTool(false);
        return;
      }

      // Use backend endpoint with token as query parameter for direct download
      // This allows using window.location for direct download without "Save As" prompt
      const downloadUrl = `${RestfulUrls.BASE_URL}${RestfulUrls.DOWNLOAD_DESKTOP_TOOL}?token=${encodeURIComponent(token)}`;
      
      // Use window.location for direct download - this often bypasses "Save As" dialog
      // Create a temporary link and trigger it
      const link = document.createElement("a");
      link.href = downloadUrl;
      link.download = "Real-time_Reporting_Desktop_Tool_PRD.exe";
      link.style.display = "none";
      
      document.body.appendChild(link);
      link.click();
      
      // Clean up after a short delay
      setTimeout(() => {
        document.body.removeChild(link);
        setIsDownloadingTool(false);
      }, 1000);
      
      // Show success message
      dispatch(showSnackbar("Download started", "success"));
    } catch (error: any) {
      console.error("Error downloading desktop tool:", error);
      dispatch(showSnackbar("Failed to download desktop tool", "error"));
      setIsDownloadingTool(false);
    }
  };

  const handleImproveMeetingPerformance = async () => {
    setIsImprovingPerformance(true);
    const url = `${RestfulUrls.BASE_URL}${RestfulUrls.IMPROVE_MEETING_PERFORMANCE}`;
    // No payload needed - API will clean all meetings
    const payload = {};

    try {
      await postDataFromApi(
        url,
        payload,
        (response: IApiResponse<any>) => {
          if (response.data?.status === "success") {
            console.log("Keep alive messages deleted successfully:", response.data);
            // Show success message using snackbar
            dispatch(showSnackbar("Data is cleaned successfully", "success"));
          }
          setIsImprovingPerformance(false);
        },
        { isAuthRequired: true },
        navigate
      );
    } catch (error) {
      console.error("Error improving meeting performance:", error);
      setIsImprovingPerformance(false);
    }
  };

  const dummyModalCloseTw = () => {};

  const getUserDetails = async () => {
    const url = `${RestfulUrls.BASE_URL}${RestfulUrls.GET_USER_DETAILS}`;
    const payloadData = null;

    postDataFromApi<any>(
      url,
      payloadData,
      (response: IApiResponse<any>) => {
        dispatch({
          type: actionTypes.UPDATE_USER_DETAILS,
          payload: response?.data?.userDetails,
        });
        dispatch({
          type: actionTypes.UPDATE_USER_PERMISSION,
          payload: response?.data?.role_permission,
        });
        localStorage.setItem(
          COMMON.USER_DETAILS_LS_KEY.DETAILS,
          JSON.stringify(response.data?.userDetails)
        );
        localStorage.setItem(
          COMMON.USER_DETAILS_LS_KEY.ROLE_PERMISSION,
          JSON.stringify(response.data?.role_permission)
        );
      },
      { isAuthRequired: true },
      navigate
    );
  };

  const getMeetingDetails = async () => {
    const url = `${RestfulUrls.BASE_URL}${RestfulUrls.MEETING_LIST}`;
    const payloadData = {};
    dispatch({
      type: actionTypes.UPDATE_MEETING_FORM_LOADING,
      payload: true,
    });
    await postDataFromApi<any>(
      url,
      payloadData,
      (response: IApiResponse<any>) => {
        dispatch({
          type: actionTypes.UPDATE_MEETING_TABLE_DATA,
          payload: response.data,
        });
        dispatch({
          type: actionTypes.UPDATE_MEETING_FORM_LOADING,
          payload: false,
        });
      },
      { isAuthRequired: true },
      navigate
    );
    dispatch({
      type: actionTypes.UPDATE_MEETING_FORM_LOADING,
      payload: false,
    });
  };

  useEffect(() => {
      getUserDetails();
      getMeetingDetails();
  }, []);

  return (
    <>
      <Box className='meetings-container'>
        <Box className='meetings-text'>Meetings</Box>
        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2, mb: 2 }}>
          {userRole === USER.USER_ROLE.SUPER_ADMIN && (
            <Button 
              variant='contained' 
              onClick={handleDownloadDesktopTool}
              disabled={isDownloadingTool}
            >
              {isDownloadingTool ? "Downloading..." : "Download Desktop Tool"}
            </Button>
          )}
          <Button 
            variant='contained' 
            onClick={handleImproveMeetingPerformance}
            disabled={isImprovingPerformance}
          >
            {isImprovingPerformance ? "Processing..." : "Improve Meeting Performance"}
          </Button>
          <Button variant='contained' onClick={handleNewMeetingButtonClick}>
            Create
          </Button>
        </Box>
        <Box className='meetings-table-container'>
          <MeetingsTable />
        </Box>
      </Box>
      <MeetingFormDilogContainer />
      <LoaderWithText
        isOpen={meetingTableLoading}
        onClose={dummyModalCloseTw}
      />
      {/* Loading overlay for Improve Meeting Performance */}
      {isImprovingPerformance && (
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 1300,
          }}
        >
          <CircularProgress size={60} sx={{ color: "white", mb: 2 }} />
          <Typography
            variant="h6"
            sx={{
              color: "white",
              fontWeight: 500,
            }}
          >
            Improving meeting performance, please wait...
          </Typography>
        </Box>
      )}
    </>
  );
};

export default MeetingsContainer;
